import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Zap, Users, CreditCard, TrendingUp, Store, Search, LogOut,
  ArrowLeft, ShieldCheck, DollarSign, Activity, Tag, Trash2, Plus,
  Eye, RefreshCw, KeyRound, UserPlus, Copy, MessageSquare, Send,
  Sun, Moon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CS = { eur: '€', usd: '$', gbp: '£', chf: 'CHF' };
const fmt = (v, c = 'eur') => `${(v || 0).toFixed(2)} ${CS[c] || '€'}`;

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('overview');
  const [chartData, setChartData] = useState([]);
  const [adminDark, setAdminDark] = useState(() => localStorage.getItem('adminTheme') !== 'light');

  const [promoCodes, setPromoCodes] = useState([]);
  const [newPromoCode, setNewPromoCode] = useState('');
  const [newPromoDays, setNewPromoDays] = useState(7);
  const [newPromoDiscount, setNewPromoDiscount] = useState(100);
  const [newPromoMinFee, setNewPromoMinFee] = useState('');
  const [creatingPromo, setCreatingPromo] = useState(false);

  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [merchantDetail, setMerchantDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailDateFrom, setDetailDateFrom] = useState('');
  const [detailDateTo, setDetailDateTo] = useState('');
  const [detailPage, setDetailPage] = useState(0);

  const [resetDialog, setResetDialog] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [resetting, setResetting] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketFilter, setTicketFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketReplyStatus, setTicketReplyStatus] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const toggleTheme = () => {
    const next = !adminDark;
    setAdminDark(next);
    localStorage.setItem('adminTheme', next ? 'dark' : 'light');
  };

  useEffect(() => {
    if (user?.role !== 'platform_admin') { navigate('/dashboard'); return; }
    const fetchData = async () => {
      try {
        const [statsRes, merchantsRes, chartRes, promoRes, ticketsRes] = await Promise.all([
          api.get('/admin/stats'), api.get('/admin/merchants?limit=100'),
          api.get('/admin/revenue-chart?days=30'), api.get('/admin/promo-codes'),
          api.get('/admin/tickets')
        ]);
        setStats(statsRes.data);
        setMerchants(merchantsRes.data.merchants);
        setChartData(chartRes.data.chart_data);
        setPromoCodes(promoRes.data.promo_codes || []);
        setTickets(ticketsRes.data.tickets || []);
      } catch { toast.error('Failed to load admin data'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [user, navigate]);

  const loadMerchantDetail = useCallback(async (storeId, page = 0) => {
    setDetailLoading(true);
    try {
      let url = `/admin/merchants/${storeId}/details?skip=${page * 20}&limit=20`;
      if (detailSearch) url += `&search=${encodeURIComponent(detailSearch)}`;
      if (detailDateFrom) url += `&date_from=${detailDateFrom}`;
      if (detailDateTo) url += `&date_to=${detailDateTo}`;
      const res = await api.get(url);
      setMerchantDetail(res.data);
    } catch { toast.error('Failed to load merchant details'); }
    finally { setDetailLoading(false); }
  }, [detailSearch, detailDateFrom, detailDateTo]);

  const openMerchantDetail = (m) => {
    setSelectedMerchant(m);
    setDetailPage(0);
    setDetailSearch('');
    setDetailDateFrom('');
    setDetailDateTo('');
    loadMerchantDetail(m.store_id, 0);
  };

  const pmLabel = (m) => {
    if (!m) return '—';
    const l = m.toLowerCase();
    if (l.includes('apple')) return 'Apple Pay';
    if (l.includes('google')) return 'Google Pay';
    if (l.includes('link')) return 'Link';
    return 'Card';
  };

  const handleResetPassword = async () => {
    if (!resetDialog) return;
    setResetting(true);
    try {
      const res = await api.post(`/admin/reset-password/${resetDialog.user_id}`);
      setResetResult(res.data);
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setResetting(false); }
  };

  const handleTicketReply = async () => {
    if (!selectedTicket || (!ticketReply.trim() && !ticketReplyStatus)) return;
    setSendingReply(true);
    try {
      const body = {};
      if (ticketReply.trim()) body.message = ticketReply;
      if (ticketReplyStatus) body.status = ticketReplyStatus;
      const res = await api.put(`/admin/tickets/${selectedTicket.ticket_id}`, body);
      setTickets(prev => prev.map(t => t.ticket_id === selectedTicket.ticket_id ? res.data : t));
      setSelectedTicket(res.data);
      setTicketReply('');
      setTicketReplyStatus('');
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reply');
    } finally { setSendingReply(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Zap className="w-6 h-6 text-orange-500 animate-pulse" /></div>;

  const themeWrap = adminDark ? 'dark' : '';

  // ============ MERCHANT DETAIL VIEW ============
  if (selectedMerchant && merchantDetail) {
    const { store, owner, staff, transactions, total_transactions, summary, promo } = merchantDetail;
    return (
      <div className={themeWrap}>
        <div className="min-h-screen bg-background text-foreground p-4 md:p-8" data-testid="admin-merchant-detail">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedMerchant(null); setMerchantDetail(null); }} className="text-muted-foreground hover:text-foreground" data-testid="back-to-merchants-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">{store?.name}</h1>
              <p className="text-xs text-muted-foreground">{owner?.email} — {store?.store_id}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Revenue', value: fmt(summary.total_revenue), icon: DollarSign, color: 'text-emerald-500' },
              { label: 'Platform Fees', value: fmt(summary.total_platform_fees), icon: Zap, color: 'text-orange-500' },
              { label: 'Stripe Fees (est.)', value: fmt(summary.total_stripe_fees), icon: CreditCard, color: 'text-blue-500' },
              { label: 'Paid Txns', value: summary.paid_count, icon: TrendingUp, color: 'text-foreground' },
            ].map(c => (
              <Card key={c.label}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <c.icon className={`w-4 h-4 ${c.color}`} />
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{c.label}</span>
                  </div>
                  <p className={`text-lg font-bold ${c.color}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{c.value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-6">
            <Card>
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">Promo Status</h3>
                {promo ? (
                  <div className="text-sm">
                    <span className="font-mono font-bold text-orange-500">{promo.code}</span>
                    <Badge className={`ml-2 text-[10px] ${promo.active ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-0' : 'bg-muted text-muted-foreground border-0'}`}>{promo.active ? 'Active' : 'Expired'}</Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {promo.discount_percent}% discount{promo.min_fee_override != null ? ` — min: ${promo.min_fee_override}€` : ''} — exp. {new Date(promo.expires_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                ) : <p className="text-sm text-muted-foreground">No promo code</p>}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2">Staff ({staff?.length || 0})</h3>
                <div className="space-y-1.5">
                  {staff?.map(s => (
                    <div key={s.user_id} className="flex items-center justify-between text-sm">
                      <span className="text-foreground">{s.name || s.email}</span>
                      <div className="flex items-center gap-2">
                        <Badge className="text-[10px] bg-muted text-muted-foreground border-0">{s.role}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => { setResetDialog(s); setResetResult(null); }} className="text-muted-foreground hover:text-orange-500 h-6 w-6 p-0" title="Reset password">
                          <KeyRound className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="mb-3">
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2 items-center">
                <Input placeholder="Search..." value={detailSearch} onChange={e => setDetailSearch(e.target.value)} className="h-9 text-sm flex-1 min-w-[140px]" data-testid="detail-search-input" />
                <Input type="date" value={detailDateFrom} onChange={e => setDetailDateFrom(e.target.value)} className="h-9 text-sm w-32" data-testid="detail-date-from" />
                <Input type="date" value={detailDateTo} onChange={e => setDetailDateTo(e.target.value)} className="h-9 text-sm w-32" data-testid="detail-date-to" />
                <Button size="sm" onClick={() => { setDetailPage(0); loadMerchantDetail(selectedMerchant.store_id, 0); }} className="bg-orange-500 hover:bg-orange-600 text-white h-9"><Search className="w-3.5 h-3.5 mr-1" /> Filter</Button>
              </div>
            </CardContent>
          </Card>

          {/* Transactions */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground text-[10px] uppercase tracking-wider">
                      <th className="text-left px-3 py-2.5">Date</th>
                      <th className="text-left px-3 py-2.5">Cashier</th>
                      <th className="text-right px-3 py-2.5">Amount</th>
                      <th className="text-right px-3 py-2.5">Platform</th>
                      <th className="text-right px-3 py-2.5">Stripe</th>
                      <th className="text-center px-3 py-2.5">Payment</th>
                      <th className="text-center px-3 py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailLoading ? (
                      <tr><td colSpan={7} className="py-8 text-center text-muted-foreground"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading...</td></tr>
                    ) : transactions?.length === 0 ? (
                      <tr><td colSpan={7} className="py-8 text-center text-muted-foreground/60">No transactions</td></tr>
                    ) : transactions?.map(txn => (
                      <tr key={txn.transaction_id} className="border-b border-border/50 hover:bg-muted/30">
                        <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{new Date(txn.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="px-3 py-2.5 text-xs text-foreground/80">{txn.created_by_name || '—'}</td>
                        <td className="px-3 py-2.5 text-right font-mono font-bold text-foreground">{txn.amount.toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-orange-500 text-xs">{(txn.platform_fee || 0).toFixed(2)}{txn.promo_applied ? ' *' : ''}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-blue-500 text-xs">~{(txn.stripe_fee_estimate || 0).toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">{pmLabel(txn.payment_method)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <Badge className={`text-[10px] border-0 ${txn.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : txn.payment_status === 'refunded' ? 'bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-muted text-muted-foreground'}`}>{txn.payment_status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total_transactions > 20 && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">{total_transactions} total</span>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" disabled={detailPage === 0} onClick={() => { const p = detailPage - 1; setDetailPage(p); loadMerchantDetail(selectedMerchant.store_id, p); }} className="text-muted-foreground h-7 text-xs">Prev</Button>
                    <Button variant="ghost" size="sm" disabled={(detailPage + 1) * 20 >= total_transactions} onClick={() => { const p = detailPage + 1; setDetailPage(p); loadMerchantDetail(selectedMerchant.store_id, p); }} className="text-muted-foreground h-7 text-xs">Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reset Password Dialog */}
          <Dialog open={!!resetDialog} onOpenChange={(o) => { if (!o) { setResetDialog(null); setResetResult(null); } }}>
            <DialogContent data-testid="reset-password-dialog">
              <DialogHeader>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogDescription>
                  {resetResult ? 'Password has been reset successfully.' : `Reset password for ${resetDialog?.name || resetDialog?.email}?`}
                </DialogDescription>
              </DialogHeader>
              {resetResult ? (
                <div className="bg-muted p-4 text-center rounded-md">
                  <p className="text-xs text-muted-foreground mb-2">New temporary password:</p>
                  <div className="flex items-center justify-center gap-2">
                    <span className="font-mono font-bold text-lg text-orange-500" data-testid="temp-password">{resetResult.temp_password}</span>
                    <Button variant="ghost" size="sm" className="text-muted-foreground h-7" onClick={() => { navigator.clipboard.writeText(resetResult.temp_password); toast.success('Copied!'); }}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Send this to the user. They should change it after login.</p>
                </div>
              ) : (
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setResetDialog(null)}>Cancel</Button>
                  <Button onClick={handleResetPassword} disabled={resetting} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="confirm-reset-btn">
                    {resetting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />} Reset Password
                  </Button>
                </DialogFooter>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // ============ MAIN ADMIN PANEL ============
  const maxRev = Math.max(...chartData.map(d => d.revenue), 1);

  return (
    <div className={themeWrap}>
      <div className="min-h-screen bg-background text-foreground p-4 md:p-8" data-testid="admin-panel">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 flex items-center justify-center rounded"><ShieldCheck className="w-5 h-5 text-white" /></div>
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>EmergencyPOS Admin</h1>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-muted-foreground hover:text-foreground" data-testid="admin-theme-toggle">
              {adminDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" onClick={logout} className="text-muted-foreground hover:text-foreground" data-testid="admin-logout-btn"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>

        <div className="flex gap-1 mb-6 border-b border-border pb-1">
          {[
            { id: 'overview', label: 'Overview', icon: Activity },
            { id: 'merchants', label: 'Merchants', icon: Store },
            { id: 'tickets', label: `Tickets${tickets.filter(t=>t.status==='open').length ? ` (${tickets.filter(t=>t.status==='open').length})` : ''}`, icon: MessageSquare },
            { id: 'promos', label: 'Promo Codes', icon: Tag },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${tab === t.id ? 'text-orange-500 border-b-2 border-orange-500 -mb-[5px]' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`admin-tab-${t.id}`}
            ><t.icon className="w-3.5 h-3.5" />{t.label}</button>
          ))}
        </div>

        {/* ===== OVERVIEW ===== */}
        {tab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Total Merchants', value: stats.total_merchants, icon: Store, color: 'text-blue-500' },
                { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-purple-500' },
                { label: 'Stripe Connected', value: stats.stripe_connected_count, icon: CreditCard, color: 'text-emerald-500' },
                { label: 'New (30d)', value: stats.recent_signups_30d, icon: UserPlus, color: 'text-cyan-500' },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1"><s.icon className={`w-4 h-4 ${s.color}`} /><span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{s.label}</span></div>
                    <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'Total Volume', value: fmt(stats.total_revenue), icon: DollarSign, color: 'text-emerald-500' },
                { label: 'Platform Fees', value: fmt(stats.platform_fees_earned), icon: Zap, color: 'text-orange-500' },
                { label: 'Stripe Fees (est.)', value: fmt(stats.stripe_fees_estimate), icon: CreditCard, color: 'text-blue-500' },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-1"><s.icon className={`w-4 h-4 ${s.color}`} /><span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{s.label}</span></div>
                    <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold mb-4 text-foreground/80">Revenue & Platform Fees (30 days)</h3>
                <div className="h-40 flex items-end gap-[2px]">
                  {chartData.map((d, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end group relative" title={`${d.date}: ${fmt(d.revenue)} / ${fmt(d.fees)} fees`}>
                      <div className="w-full bg-orange-500/30 hover:bg-orange-500/50 transition-colors relative" style={{ height: `${Math.max(d.revenue / maxRev * 100, 2)}%`, minHeight: '2px' }}>
                        <div className="absolute bottom-0 left-0 right-0 bg-orange-500" style={{ height: `${Math.max(d.fees / (d.revenue || 1) * 100, 5)}%`, minHeight: '1px' }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
                  <span><span className="inline-block w-2 h-2 bg-orange-500/30 mr-1" />Revenue</span>
                  <span><span className="inline-block w-2 h-2 bg-orange-500 mr-1" />Platform Fees</span>
                </div>
              </CardContent>
            </Card>

            {/* Top Merchants & Payment Methods */}
            <div className="grid md:grid-cols-2 gap-3">
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-sm font-bold mb-3 text-foreground/80">Top Merchants by Revenue</h3>
                  {stats.top_merchants?.length > 0 ? (
                    <div className="space-y-2">
                      {stats.top_merchants.map((m, i) => (
                        <div key={m.store_id} className="flex items-center gap-3">
                          <span className="text-xs font-bold text-muted-foreground w-5">{i + 1}.</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-foreground">{m.store_name}</p>
                            <p className="text-[10px] text-muted-foreground">{m.count} paid txns</p>
                          </div>
                          <span className="text-sm font-bold text-emerald-500 font-mono">{m.revenue.toFixed(2)} &euro;</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No paid transactions yet</p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-sm font-bold mb-3 text-foreground/80">Payment Methods</h3>
                  {stats.payment_method_breakdown?.length > 0 ? (
                    <div className="space-y-2.5">
                      {stats.payment_method_breakdown.map(pm => {
                        const maxVol = Math.max(...(stats.payment_method_breakdown || []).map(p => p.volume), 1);
                        return (
                          <div key={pm.method}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-foreground font-medium">{pm.method}</span>
                              <span className="text-xs text-muted-foreground">{pm.count} txn{pm.count > 1 ? 's' : ''} — {pm.volume.toFixed(2)} &euro;</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-orange-500 rounded-full transition-all" style={{ width: `${Math.max(pm.volume / maxVol * 100, 3)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No data yet</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Scheduled Jobs Trigger */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-bold mb-3 text-foreground/80">Scheduled Jobs</h3>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="admin-trigger-daily-summary"
                    onClick={async () => {
                      try {
                        await api.post('/admin/trigger-daily-summary');
                        toast.success('Daily summary job triggered');
                      } catch { toast.error('Failed to trigger daily summary'); }
                    }}
                    className="text-xs"
                  >
                    <Send className="w-3 h-3 mr-1" /> Trigger Daily Summary
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="admin-trigger-monthly-report"
                    onClick={async () => {
                      try {
                        await api.post('/admin/trigger-monthly-report');
                        toast.success('Monthly report job triggered');
                      } catch { toast.error('Failed to trigger monthly report'); }
                    }}
                    className="text-xs"
                  >
                    <Send className="w-3 h-3 mr-1" /> Trigger Monthly Report
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">Daily summary: 06:00 UTC · Monthly report: 1st of month at 07:00 UTC</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== MERCHANTS ===== */}
        {tab === 'merchants' && (
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search merchants..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-10" data-testid="admin-search-merchants" />
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground text-[10px] uppercase tracking-wider">
                        <th className="text-left px-3 py-3">Store</th>
                        <th className="text-left px-3 py-3">Owner</th>
                        <th className="text-center px-3 py-3">Txns</th>
                        <th className="text-right px-3 py-3">Revenue</th>
                        <th className="text-right px-3 py-3">Fees</th>
                        <th className="text-center px-3 py-3">Promo</th>
                        <th className="text-center px-3 py-3">Stripe</th>
                        <th className="text-right px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {merchants.filter(m => !search || m.store_name.toLowerCase().includes(search.toLowerCase()) || m.owner_email.toLowerCase().includes(search.toLowerCase())).map(m => (
                        <tr key={m.store_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => openMerchantDetail(m)} data-testid={`merchant-row-${m.store_id}`}>
                          <td className="px-3 py-3 font-medium text-foreground text-xs">{m.store_name}</td>
                          <td className="px-3 py-3 text-muted-foreground text-xs">{m.owner_email}</td>
                          <td className="px-3 py-3 text-center text-foreground/80 text-xs">{m.transaction_count}</td>
                          <td className="px-3 py-3 text-right font-mono font-bold text-emerald-500 text-xs">{fmt(m.total_revenue)}</td>
                          <td className="px-3 py-3 text-right font-mono text-orange-500 text-xs">{fmt(m.platform_fees)}</td>
                          <td className="px-3 py-3 text-center">
                            {m.promo_code ? <Badge className={`text-[10px] border-0 ${m.promo_active ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400' : 'bg-muted text-muted-foreground'}`}>{m.promo_code}</Badge> : <span className="text-muted-foreground/60 text-xs">—</span>}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <Badge className={`text-[10px] border-0 ${m.stripe_connected ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{m.stripe_connected ? 'Yes' : 'No'}</Badge>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground h-7 text-xs" onClick={e => { e.stopPropagation(); openMerchantDetail(m); }}><Eye className="w-3.5 h-3.5" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== TICKETS ===== */}
        {tab === 'tickets' && (
          <div className="space-y-4" data-testid="admin-tickets-section">
            <div className="flex gap-2 flex-wrap">
              {['all', 'open', 'in_progress', 'resolved', 'closed'].map(f => (
                <button key={f} onClick={() => setTicketFilter(f)}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${ticketFilter === f ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
                  data-testid={`ticket-filter-${f}`}
                >{f === 'all' ? 'All' : f === 'in_progress' ? 'In Progress' : f.charAt(0).toUpperCase() + f.slice(1)} ({f === 'all' ? tickets.length : tickets.filter(t => t.status === f).length})</button>
              ))}
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground text-[10px] uppercase tracking-wider">
                        <th className="text-left px-3 py-2.5">Subject</th>
                        <th className="text-left px-3 py-2.5">From</th>
                        <th className="text-center px-3 py-2.5">Category</th>
                        <th className="text-center px-3 py-2.5">Status</th>
                        <th className="text-center px-3 py-2.5">Replies</th>
                        <th className="text-left px-3 py-2.5">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.filter(t => ticketFilter === 'all' || t.status === ticketFilter).map(t => (
                        <tr key={t.ticket_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer" onClick={() => { setSelectedTicket(t); setTicketReply(''); setTicketReplyStatus(''); }} data-testid={`admin-ticket-${t.ticket_id}`}>
                          <td className="px-3 py-2.5 text-xs font-medium text-foreground max-w-[200px] truncate">{t.subject}</td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground">
                            <div>{t.user_name}</div>
                            <div className="text-[10px] text-muted-foreground/60">{t.store_name}</div>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <Badge className="text-[10px] bg-muted text-foreground border-0">{t.category}</Badge>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            <Badge className={`text-[10px] border-0 ${t.status === 'open' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' : t.status === 'in_progress' ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400' : t.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}>{t.status === 'in_progress' ? 'In Progress' : t.status}</Badge>
                          </td>
                          <td className="px-3 py-2.5 text-center text-xs text-muted-foreground">{t.replies?.length || 0}</td>
                          <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">{new Date(t.created_at).toLocaleDateString('fr-FR')}</td>
                        </tr>
                      ))}
                      {tickets.filter(t => ticketFilter === 'all' || t.status === ticketFilter).length === 0 && (
                        <tr><td colSpan={6} className="py-8 text-center text-muted-foreground/60">No tickets</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Ticket Detail + Reply Dialog */}
            <Dialog open={!!selectedTicket} onOpenChange={(o) => { if (!o) setSelectedTicket(null); }}>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="admin-ticket-detail-dialog">
                {selectedTicket && (
                  <>
                    <div className="space-y-1 mb-3">
                      <h3 className="font-bold text-sm">{selectedTicket.subject}</h3>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span>{selectedTicket.user_name} ({selectedTicket.user_email})</span>
                        <Badge className="text-[10px] bg-muted text-foreground border-0">{selectedTicket.category}</Badge>
                        <Badge className={`text-[10px] border-0 ${selectedTicket.status === 'open' ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400' : selectedTicket.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-orange-500/20 text-orange-600 dark:text-orange-400'}`}>{selectedTicket.status}</Badge>
                      </div>
                    </div>
                    <div className="bg-muted p-3 text-sm whitespace-pre-wrap rounded-md mb-3">{selectedTicket.message}</div>
                    {selectedTicket.replies?.map(r => (
                      <div key={r.reply_id} className={`border-l-2 pl-3 py-1 mb-2 ${r.from === 'admin' ? 'border-orange-500' : 'border-border'}`}>
                        <p className="text-[10px] font-medium text-muted-foreground mb-0.5">{r.from_name} — {new Date(r.created_at).toLocaleString('fr-FR')}</p>
                        <p className="text-sm whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                    <div className="border-t border-border pt-3 mt-3 space-y-2">
                      <textarea value={ticketReply} onChange={e => setTicketReply(e.target.value)} placeholder="Write a reply..." className="w-full min-h-[80px] bg-muted border border-border text-foreground text-sm p-2 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-orange-500" data-testid="admin-ticket-reply-input" />
                      <div className="flex items-center gap-2">
                        <select value={ticketReplyStatus} onChange={e => setTicketReplyStatus(e.target.value)} className="bg-muted border border-border text-foreground text-xs h-8 px-2 rounded" data-testid="admin-ticket-status-select">
                          <option value="">No status change</option>
                          <option value="in_progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                          <option value="closed">Closed</option>
                        </select>
                        <Button size="sm" onClick={handleTicketReply} disabled={sendingReply} className="bg-orange-500 hover:bg-orange-600 text-white h-8 ml-auto" data-testid="admin-ticket-send-reply-btn">
                          <Send className="w-3.5 h-3.5 mr-1" />{sendingReply ? 'Sending...' : 'Send Reply'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* ===== PROMO CODES ===== */}
        {tab === 'promos' && (
          <div className="space-y-6" data-testid="admin-promos-section">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4">Create Promo Code</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Code</label>
                    <Input value={newPromoCode} onChange={e => setNewPromoCode(e.target.value.toUpperCase())} placeholder="EPOS7FREE" className="h-10 font-mono uppercase" data-testid="admin-promo-code-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Days</label>
                    <Input type="number" value={newPromoDays} onChange={e => setNewPromoDays(parseInt(e.target.value) || 7)} min={1} className="h-10" data-testid="admin-promo-days-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Discount %</label>
                    <Input type="number" value={newPromoDiscount} onChange={e => setNewPromoDiscount(parseInt(e.target.value) || 0)} min={0} max={100} className="h-10" data-testid="admin-promo-discount-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Min fee</label>
                    <Input value={newPromoMinFee} onChange={e => setNewPromoMinFee(e.target.value)} placeholder="0.10 default" className="h-10" data-testid="admin-promo-minfee-input" />
                  </div>
                  <Button onClick={async () => {
                    if (!newPromoCode.trim()) { toast.error('Enter a code'); return; }
                    setCreatingPromo(true);
                    try {
                      const p = { code: newPromoCode, trial_days: newPromoDays, discount_percent: newPromoDiscount };
                      if (newPromoMinFee !== '') p.min_fee_override = parseFloat(newPromoMinFee);
                      const res = await api.post('/admin/promo-codes', p);
                      setPromoCodes(prev => [res.data, ...prev]);
                      setNewPromoCode('');
                      toast.success(`"${res.data.code}" created!`);
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                    finally { setCreatingPromo(false); }
                  }} disabled={creatingPromo} className="bg-orange-500 hover:bg-orange-600 text-white h-10" data-testid="admin-create-promo-btn">
                    <Plus className="w-4 h-4 mr-1" /> Create
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">100% = free, 50% = half-price. Min fee: empty = default (0.10), 0 = no min.</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[500px]">
                    <thead>
                      <tr className="border-b border-border text-muted-foreground text-[10px] uppercase tracking-wider">
                        <th className="text-left px-4 py-3">Code</th>
                        <th className="text-center px-3 py-3">Days</th>
                        <th className="text-center px-3 py-3">Discount</th>
                        <th className="text-center px-3 py-3">Min Fee</th>
                        <th className="text-center px-3 py-3">Uses</th>
                        <th className="text-center px-3 py-3">Status</th>
                        <th className="text-right px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoCodes.map(p => (
                        <tr key={p.code} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="px-4 py-3 font-mono font-bold text-foreground" data-testid={`promo-code-${p.code}`}>{p.code}</td>
                          <td className="px-3 py-3 text-center text-foreground/80">{p.trial_days}d</td>
                          <td className="px-3 py-3 text-center text-foreground/80">{p.discount_percent ?? 100}%</td>
                          <td className="px-3 py-3 text-center text-foreground/80">{p.min_fee_override != null ? `${p.min_fee_override}€` : 'Def.'}</td>
                          <td className="px-3 py-3 text-center text-foreground/80">{p.uses || 0}</td>
                          <td className="px-3 py-3 text-center"><Badge className={`border-0 text-[10px] ${p.active ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : 'bg-red-500/20 text-red-600 dark:text-red-400'}`}>{p.active ? 'Active' : 'Off'}</Badge></td>
                          <td className="px-4 py-3 text-right">
                            {p.active && <Button variant="ghost" size="sm" onClick={async () => {
                              try { await api.delete(`/admin/promo-codes/${p.code}`); setPromoCodes(prev => prev.map(pc => pc.code === p.code ? { ...pc, active: false } : pc)); toast.success(`"${p.code}" deactivated`); } catch { toast.error('Failed'); }
                            }} className="text-red-500 hover:text-red-400 h-7 text-xs"><Trash2 className="w-3 h-3 mr-1" /> Off</Button>}
                          </td>
                        </tr>
                      ))}
                      {promoCodes.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-muted-foreground">No promo codes</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
